"use client";

import * as React from "react";
import { Brain, HeartHandshake, Users, House, UserCheck, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
const services = [{
  id: 1,
  title: "Psychogeriatrische zorg",
  shortDesc: "Gespecialiseerde zorg en begeleiding voor mensen met dementie in hun eigen, vertrouwde omgeving.",
  fullDesc: "Wij bieden gespecialiseerde psychogeriatrische zorg, met een sterke focus op dementiezorg. Onze vaste zorgverleners creëren een veilige, herkenbare omgeving en bieden structuur, begeleiding en verzorging die is afgestemd op de specifieke fase van dementie. We hebben hierbij ook nadrukkelijk oog voor de belasting van mantelzorgers en bieden hen de nodige ondersteuning en advies.",
  Icon: Brain
}, {
  id: 2,
  title: "Psychische ondersteuning",
  shortDesc: "Begeleiding en ondersteuning bij psychische en psychiatrische problematiek thuis.",
  fullDesc: "Voor cliënten met psychische of psychiatrische kwetsbaarheden bieden wij stabiliteit en structuur in de thuissituatie. Onze gespecialiseerde begeleiders helpen bij het aanbrengen van een gezond dagritme, het omgaan met emoties en het vergroten van de zelfredzaamheid. Dit doen wij altijd met groot respect voor uw persoonlijke situatie, leefwijze en achtergrond.",
  Icon: HeartHandshake
}, {
  id: 3,
  title: "Begeleiding",
  shortDesc: "Hulp bij het structureren van de dag en het behouden van de regie over uw eigen leven.",
  fullDesc: "Onze begeleiding is erop gericht om uw zelfstandigheid zo lang mogelijk te behouden of te vergroten. We ondersteunen u bij dagelijkse activiteiten, het op orde houden van uw administratie, het opbouwen of onderhouden van een sociaal netwerk en het voorbereiden van afspraken. Samen kijken we naar wat u zelf kunt en waar wij u passend kunnen aanvullen.",
  Icon: Users
}, {
  id: 4,
  title: "Huishoudelijke hulp",
  shortDesc: "Praktische en betrouwbare ondersteuning voor een schoon, veilig en leefbaar thuis.",
  fullDesc: "Een schoon en opgeruimd huis draagt aanzienlijk bij aan uw welzijn en rust. Onze betrouwbare medewerkers nemen huishoudelijke taken van u over, zoals schoonmaken, stofzuigen, wassen en strijken. We werken zoveel mogelijk met vaste gezichten, zodat u altijd weet wie er bij u over de vloer komt en er een vertrouwensband kan ontstaan.",
  Icon: House
}, {
  id: 5,
  title: "Persoonlijke verzorging",
  shortDesc: "Respectvolle en deskundige hulp bij uw dagelijkse persoonlijke verzorging.",
  fullDesc: "Wij ondersteunen u bij uw dagelijkse verzorging, zoals hulp bij het opstaan, wassen, aankleden, douchen en de verzorging van huid en haar. Onze gediplomeerde zorgprofessionals werken met oprechte aandacht en hebben diep respect voor uw privacy, dagelijkse gewoonten en culturele of religieuze achtergrond.",
  Icon: UserCheck
}, {
  id: 6,
  title: "Persoonsgerichte zorg",
  shortDesc: "Zorg die volledig is afgestemd op uw unieke wensen, behoeften en achtergrond.",
  fullDesc: "Bij CuraNova Zorg staat u als mens centraal. Wij geloven niet in standaardoplossingen, maar in zorg die naadloos aansluit bij uw leven. We houden nadrukkelijk rekening met uw religieuze, culturele en persoonlijke achtergrond en streven ernaar om met een klein, vast team van zorgverleners te werken voor maximale continuïteit, herkenbaarheid en vertrouwen.",
  Icon: Activity
}];
export default function OnzeZorgdiensten() {
  return <section id="onze-zorgdiensten" className="bg-background py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
            Onze Zorgdiensten
          </h2>
          <p className="text-lg text-muted-foreground">
            CuraNova Zorg biedt een breed scala aan extramurale zorg en ondersteuning aan huis. 
            Wij leveren maatwerk, afgestemd op uw persoonlijke situatie en behoeften.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {services.map((service, i) => {
          const Icon = service.Icon;
          return <Card key={service.id} data-index={i} className="flex flex-col h-full border-border/50 shadow-sm hover:shadow-md transition-shadow duration-300 bg-card">
                <CardHeader>
                  <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-primary" aria-hidden="true" />
                  </div>
                  <CardTitle className="text-xl text-foreground">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-grow">
                  <CardDescription className="text-base text-muted-foreground">
                    {service.shortDesc}
                  </CardDescription>
                </CardContent>
                <CardFooter className="pt-4 border-t border-border/50 mt-auto">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" className="w-full justify-between group hover:bg-primary/5 hover:text-primary">
                        Lees meer
                        <span className="bg-primary/10 text-primary rounded-full w-6 h-6 flex items-center justify-center group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          +
                        </span>
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[500px]">
                      <DialogHeader>
                        <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-4">
                          <Icon className="w-6 h-6 text-primary" aria-hidden="true" />
                        </div>
                        <DialogTitle className="text-2xl font-serif text-foreground mb-2">
                          {service.title}
                        </DialogTitle>
                        <DialogDescription className="text-base text-foreground/80 leading-relaxed">
                          {service.fullDesc}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="mt-6 flex justify-end">
                        <Button variant="default" className="w-full sm:w-auto">
                          Bespreek uw situatie
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                </CardFooter>
              </Card>;
        })}
        </div>
      </div>
    </section>;
}